import { ChangeDetectionStrategy, Component, effect, inject, input, output, untracked } from '@angular/core';
import { FormArray, FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { SharedMaterialUIModule } from '@shared/modules/shared-material-ui.module';
import { scrollToFirstInvalid } from '@shared/utils/scroll-to-invalid';
import { CounsellingIntakeAssessmentResponse, SaveCounsellingIntakeAssessmentRequest } from '../../cia.model';
import { LookupResponse } from '@core/models/common.model';
import { CollectionName, collectionRows, createCollectionGroup, fixedRowPayload, fixedRowsKey, rebuildFixedRows } from '../../cia-fixed-rows';
import {
  CiaFormState,
  CiaPart,
  createClinicalBackgroundGroup,
  createFunctionalRiskGroup,
  createPresentingGroup,
  createTreatmentGroup,
} from '../../cia-form-groups';
import { lookupItems } from '@core/lookup/lookup-utils';
import { currentDateAndTime, toNullableDateString } from '@shared/utils/date-utils';
import { cleanRecord } from '@shared/utils/payload-utils';
import { functionalRiskValue, presentingValue, treatmentValue } from '../../cia-populate';
import { CiaStepBackgroundComponent } from '../cia-step-background/cia-step-background.component';
import { CiaStepFunctionalComponent } from '../cia-step-functional/cia-step-functional.component';
import { CiaStepPresentingComponent } from '../cia-step-presenting/cia-step-presenting.component';
import { CiaStepTreatmentComponent } from '../cia-step-treatment/cia-step-treatment.component';

// The clinical assessment inside the case's wizard (MW-188: clinical judgement only). The wizard of the case shows one part of it at a time:
// Presenting problem, Clinical assessment, Functional impact and risk, Treatment planning. This component holds what the parts share: the form
// groups (one for each part, so each part is complete on its own), the inputs and the request. What each part shows is a component of its own;
// the rules for rows, reference lists, filling the form and building the request live in the cia-*.ts files beside it. Who the member is, the
// consent, the referral preferences and the referrer are recorded on the intervention, not here.
@Component({
  selector: 'counselling-intake-assessment-form',
  imports: [
    ReactiveFormsModule,
    CiaStepBackgroundComponent,
    CiaStepFunctionalComponent,
    CiaStepPresentingComponent,
    CiaStepTreatmentComponent,
    ...SharedMaterialUIModule,
  ],
  templateUrl: './cia-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CounsellingIntakeAssessmentFormComponent {
  private readonly formBuilder = inject(FormBuilder);

  // The part of the assessment that is shown.
  readonly part = input.required<CiaPart>();
  readonly readOnly = input(false);
  readonly initialValue = input<CounsellingIntakeAssessmentResponse | null>(null);
  readonly lookups = input<Record<string, LookupResponse[]>>({});
  readonly submitted = output<SaveCounsellingIntakeAssessmentRequest>();

  // The Intervention this assessment attaches to; the member is read through its owner.
  readonly interventionId = input<string | null>(null);
  // The Suicide Risk Screener (MW19) completed for this case, if any: shown with the risk rating, and a new assessment starts
  // with its screening ticked.
  readonly riskScreener = input<{ screenedDate: string; riskRatingName: string } | null>(null);

  protected isAttachMode(): boolean {
    return !this.initialValue();
  }

  // Part 1, Presenting problem: when the intake was, the problem and its characteristics.
  readonly presentingForm = createPresentingGroup(this.formBuilder);
  // Part 2, Clinical assessment: the 4P grid, the life stages and the patterns.
  readonly clinicalBackgroundForm = createClinicalBackgroundGroup(this.formBuilder);
  // Part 3, Functional impact and risk: the underlying problems, the work and functional impact, the screenings and the risk rating.
  readonly functionalRiskForm = createFunctionalRiskGroup(this.formBuilder);
  // Part 4, Treatment planning: the limitations, the P7 planning rows, the clinical impressions and the treatment plan.
  readonly treatmentForm = createTreatmentGroup(this.formBuilder);

  // All the groups together, so the whole assessment has one validity and one value.
  readonly form = this.formBuilder.group({
    presenting: this.presentingForm,
    clinicalBackground: this.clinicalBackgroundForm,
    functionalRisk: this.functionalRiskForm,
    treatment: this.treatmentForm,
  });

  // What the parts share outside the groups: the last selection of the stand-alone choice (see CiaFormState).
  protected readonly state: CiaFormState = { previousUnderlyingProblems: [] };
  // What the fixed rows were last built from, so they are rebuilt only when the lists or the assessment change.
  private fixedRowsKey = '';

  constructor() {
    // Fill the forms from the saved assessment (or start a new one) whenever it changes; only the assessment is watched, so what the user has
    // typed is not replaced when something else (the screener, read-only) changes.
    effect(() => {
      const item = this.initialValue();
      untracked(() => this.populate(item));
    });
    // Build the rows that come from a list once the lists have arrived.
    effect(() => {
      const item = this.initialValue();
      this.lookups();
      untracked(() => this.syncFixedRows(item));
    });
    // Tick the suicide screening when the Suicide Risk Screener was done.
    effect(() => {
      const screener = this.riskScreener();
      this.lookups();
      untracked(() => this.addSuicideScreeningRow(screener));
    });
  }

  // Whether something has been entered since the assessment was opened or last saved.
  hasChanges(): boolean {
    return this.form.dirty;
  }

  // Saves what has been entered so far (a part is not complete yet): only the integrity of the entries is checked, such as a symptom cluster
  // that has text but no ratings. Completeness is checked when the assessment is locked. Returns the part that needs attention instead when
  // something cannot be saved, or null once the request has been sent.
  requestSave(): CiaPart | null {
    if (this.presentingForm.invalid) {
      this.presentingForm.markAllAsTouched();
      return 'presenting';
    }

    if (this.treatmentForm.invalid) {
      this.treatmentForm.markAllAsTouched();
      return 'treatment';
    }

    this.emitRequest();
    return null;
  }

  // Scrolls to what is missing in the part that is shown (it has to be drawn first).
  showFirstInvalidField(): void {
    setTimeout(() => scrollToFirstInvalid(), 150);
  }

  // The rows of one collection (screenings, limitations, ...).
  private rows(name: CollectionName): FormArray {
    return collectionRows(name === 'riskScreenings' ? this.functionalRiskForm : this.treatmentForm, name);
  }

  // Adds one row to a collection.
  private add(name: CollectionName, value?: Record<string, unknown>): void {
    this.rows(name).push(createCollectionGroup(this.formBuilder, name, value));
  }

  // A new assessment starts with the screening of the Suicide Risk Screener ticked. The rows are rebuilt whenever the member or the
  // lookups arrive, so this also runs at the end of that rebuild and only adds the row when it is not there.
  private addSuicideScreeningRow(screener: { screenedDate: string; riskRatingName: string } | null): void {
    if (!screener || !this.isAttachMode()) {
      return;
    }

    const type = lookupItems(this.lookups(), 'riskScreeningTypes').find((item) => item.code === 'SUICIDAL_THOUGHTS_OF_HURTING_SELF_OR_OTHERS');
    if (!type) {
      return;
    }

    const present = this.rows('riskScreenings').controls.some((row) => row.get('riskScreeningTypeId')?.value === type.id);
    if (present) {
      return;
    }

    this.add('riskScreenings', {
      riskScreeningTypeId: type.id,
      resultSummary: screener.riskRatingName,
      notes: `Suicide Risk Screener (MW19) completed ${screener.screenedDate}`,
    });
  }

  // Builds the rows that come from a list once the lists are there, and again when another assessment is opened.
  private syncFixedRows(item: CounsellingIntakeAssessmentResponse | null): void {
    const key = fixedRowsKey(this.lookups(), item);
    if (key === null || key === this.fixedRowsKey) {
      return;
    }

    this.fixedRowsKey = key;
    rebuildFixedRows(this.formBuilder, this.lookups(), item, {
      clinicalBackground: this.clinicalBackgroundForm,
      functionalRisk: this.functionalRiskForm,
      treatment: this.treatmentForm,
    });

    if (this.readOnly()) {
      this.form.disable({ emitEvent: false });
    }
  }

  private emitRequest(): void {
    const value = this.form.getRawValue();
    const request = {
      ...cleanRecord(value.presenting),
      ...cleanRecord(value.functionalRisk),
      ...cleanRecord(value.treatment),
      ...fixedRowPayload(value),
      intakeDate: toNullableDateString(value.presenting.intakeDate as string | Date | null | undefined),
      intakeTime: value.presenting.intakeTime ? `${value.presenting.intakeTime}:00` : null,
      rowVersion: this.initialValue()?.rowVersion ?? null,
    } as SaveCounsellingIntakeAssessmentRequest;

    this.submitted.emit(request);
  }

  // Fills the forms from a saved assessment, or starts a new one at the current date and time.
  private populate(item: CounsellingIntakeAssessmentResponse | null): void {
    if (!item) {
      const now = currentDateAndTime();
      this.presentingForm.patchValue(
        {
          intakeDate: this.presentingForm.controls.intakeDate.value || now.date,
          intakeTime: this.presentingForm.controls.intakeTime.value || now.time,
        },
        { emitEvent: false },
      );
    }

    if (item) {
      this.presentingForm.patchValue(presentingValue(item), { emitEvent: false });
      this.functionalRiskForm.patchValue(functionalRiskValue(item), { emitEvent: false });
      this.treatmentForm.patchValue(treatmentValue(item), { emitEvent: false });
      this.state.previousUnderlyingProblems = this.functionalRiskForm.controls.underlyingProblemIds.value ?? [];
    }

    const collections: CollectionName[] = ['riskScreenings'];

    for (const name of collections) {
      const rows = this.rows(name);
      rows.clear();
      for (const row of item?.[name] ?? []) {
        this.add(name, row as Record<string, unknown>);
      }
    }

    this.addSuicideScreeningRow(this.riskScreener());

    if (this.readOnly()) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }

    // What the server holds is now what is on screen.
    this.form.markAsPristine();
  }
}
